import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { STATE_OPTIONS, JOURNEY_OPTIONS, QUALIFIERS, CONSENT_TEXT } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RegistrationForm = ({ attribution, variant = "hero" }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", state: "", journey: "", vaBenefitUsage: "", householdIncome: "", buyTimeline: "" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const id = (name) => `${variant}-${name}`;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.vaBenefitUsage || !form.householdIncome || !form.buyTimeline) {
      setError("Please answer the three questions at the top.");
      return;
    }
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.state) {
      setError("Please complete all required fields.");
      return;
    }
    if (!consent) {
      setError("Please check the consent box to register.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/ghl-lead`, {
        ...form,
        consent: true,
        consentTimestamp: new Date().toISOString(),
        consentText: CONSENT_TEXT,
        ...attribution,
      });
      navigate(`/thank-you?name=${encodeURIComponent(form.firstName)}`);
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong. Please check your details and try again.");
    }
  };

  const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-md px-4 py-3 text-[15px] text-white placeholder:text-white focus:outline-none focus:border-[#FE0000]/60 transition-colors duration-200";

  return (
    <form onSubmit={submit} noValidate data-testid={id("registration-form")}>
      {QUALIFIERS.map((q) => (
        <div key={q.key} className="mb-3">
          <label htmlFor={id(q.key)} className="block text-[13px] text-white mb-1.5">{q.label}</label>
          <select
            id={id(q.key)}
            data-testid={id(`${q.key}-select`)}
            className={`${inputCls} appearance-none ${form[q.key] ? "" : "text-white"}`}
            value={form[q.key]}
            onChange={set(q.key)}
          >
            <option value="" disabled>Select one option*</option>
            {q.options.map((o) => <option key={o} value={o} className="bg-[#182f56]">{o}</option>)}
          </select>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-3">
        <input data-testid={id("first-name-input")} className={inputCls} placeholder="First Name*" value={form.firstName} onChange={set("firstName")} />
        <input data-testid={id("last-name-input")} className={inputCls} placeholder="Last Name*" value={form.lastName} onChange={set("lastName")} />
      </div>
      <input data-testid={id("email-input")} type="email" className={`${inputCls} mt-3`} placeholder="Email Address*" value={form.email} onChange={set("email")} />
      <input data-testid={id("phone-input")} type="tel" className={`${inputCls} mt-3`} placeholder="Mobile Phone*" value={form.phone} onChange={set("phone")} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <select data-testid={id("state-select")} className={`${inputCls} appearance-none ${form.state ? "" : "text-white"}`} value={form.state} onChange={set("state")}>
          <option value="" disabled>State*</option>
          {STATE_OPTIONS.map((s) => <option key={s} value={s} className="bg-[#182f56]">{s}</option>)}
        </select>
        <select data-testid={id("journey-select")} className={`${inputCls} appearance-none ${form.journey ? "" : "text-white"}`} value={form.journey} onChange={set("journey")}>
          <option value="">Homebuying Journey (optional)</option>
          {JOURNEY_OPTIONS.map((s) => <option key={s} value={s} className="bg-[#182f56]">{s}</option>)}
        </select>
      </div>
      <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid={id("consent-label")}>
        <input
          data-testid={id("consent-checkbox")}
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#FE0000] bg-transparent"
        />
        <span className="text-[13px] leading-relaxed text-white">{CONSENT_TEXT}</span>
      </label>
      {error && <p data-testid={id("form-error")} className="text-xs text-red-400 mt-3">{error}</p>}
      <button
        data-testid={id("submit-button")}
        data-cta
        type="submit"
        disabled={status === "loading"}
        className="w-full mt-4 bg-[#FFE100] text-[#12264A] font-bold text-sm tracking-wide uppercase py-4 rounded-md btn-slide active:scale-[0.99] disabled:opacity-60"
      >
        {status === "loading" ? "RESERVING..." : "✓ RESERVE MY FREE SEAT"}
      </button>
      <p className="mono text-[13px] text-white text-center mt-3 tracking-wider">
        Free educational event · No obligation · No hard credit pull required to attend
      </p>
    </form>
  );
};
